import React from 'react';
import { motion } from 'framer-motion';
import { Play } from 'lucide-react';
import { type ModuleData, getModuleDisplayInfo, getModuleColors, getModuleIcon } from '../../content';
import { GroupId } from '../../config/systemConfig';
import { getGrowthStage } from '../../mastery';
import { DjayAvatar } from '../../mascot';
import type { DjayVariant } from '../../mascot';
interface AdventureCTAProps {
  module: ModuleData;
  selectedGroup: GroupId;
  onStart: () => void;
  getModuleMasteryForGroup: (moduleId: string) => number;
  djayVariant?: DjayVariant;
}
export function AdventureCTA({
  module,
  selectedGroup,
  onStart,
  getModuleMasteryForGroup,
  djayVariant = 'default'
}: AdventureCTAProps) {
  const displayInfo = getModuleDisplayInfo(module.id, selectedGroup);
  const title = displayInfo?.title || module.title;
  const mastery = getModuleMasteryForGroup(module.id);
  const growthStage = getGrowthStage(mastery);
  const colors = getModuleColors(module.color);
  const Icon = getModuleIcon(module.icon);
  let message = 'Ga verder waar je gebleven was';
  if (mastery < 20) message = 'Je bent goed begonnen!';else if (mastery < 50) message = 'Je bent lekker op weg!';else if (mastery < 80) message = 'Nog even doorzetten!';else message = 'Bijna klaar met dit vak!';
  return <motion.div whileHover={{
    y: -4,
    scale: 1.01
  }} whileTap={{
    scale: 0.98
  }} className="relative cursor-pointer group" onClick={onStart} data-id="element-1070">
      {/* Main Banner */}
      <div className={`relative overflow-hidden rounded-[2rem] bg-gradient-to-r ${colors.from} ${colors.to} p-1 shadow-lg`} data-id="element-1071">
        <div className="bg-white/10 backdrop-blur-sm rounded-[1.8rem] p-3 sm:p-6 flex flex-row items-center gap-3 sm:gap-6 relative z-10" data-id="element-1072">
          {/* Left: Icon & Progress */}
          <div className="relative flex-shrink-0" data-id="element-1073">
            <div className={`w-14 h-14 sm:w-20 sm:h-20 bg-white rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-md transform -rotate-6 group-hover:rotate-0 transition-transform duration-300`} data-id="element-1074">
              <Icon className={`w-7 h-7 sm:w-10 sm:h-10 ${colors.text}`} strokeWidth={2.5} data-id="element-1075" />
            </div>
            <div className="absolute -bottom-2 -right-2 sm:-bottom-3 sm:-right-3 bg-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full shadow-sm border-2 border-slate-50 flex items-center gap-1" data-id="element-1076">
              <span className="text-xs sm:text-sm" data-id="element-1077">{growthStage.emoji}</span>
              <span className="text-[10px] sm:text-xs font-black text-slate-700" data-id="element-1078">
                {Math.round(mastery)}%
              </span>
            </div>
          </div>

          {/* Center: Text Content */}
          <div className="flex-1 text-left text-white" data-id="element-1079">
            <div className="text-white/80 text-[10px] sm:text-sm font-bold uppercase tracking-wider mb-0.5 sm:mb-1" data-id="element-1080">
              Jouw Avontuur
            </div>
            <h3 className="text-lg sm:text-3xl font-black tracking-tight mb-0.5 sm:mb-1 drop-shadow-sm leading-tight" data-id="element-1081">
              {title}
            </h3>
            <p className="text-white/90 font-medium text-xs sm:text-lg leading-snug" data-id="element-1082">
              {message}
            </p>
          </div>

          {/* Right: Play Button */}
          <div className="flex-shrink-0" data-id="element-1083">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300" data-id="element-1084">
              <Play className={`w-6 h-6 sm:w-8 sm:h-8 ${colors.text} ml-1`} fill="currentColor" data-id="element-1085" />
            </div>
          </div>
        </div>

        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-1086" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/10 rounded-full blur-2xl translate-y-1/4 -translate-x-1/4" data-id="element-1087" />
      </div>

      {/* Djay Peeking */}
      <motion.div className="absolute -bottom-2 -left-2 sm:-left-6 pointer-events-none z-20" animate={{
      y: [0, -4, 0],
      rotate: [-12, -8, -12]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-1088">
        <DjayAvatar size="sm" emotion="pointing" variant={djayVariant} animate data-id="element-1089" />
      </motion.div>
    </motion.div>;
}